import { Link } from "@tanstack/react-router";
import { type RuleGroup, describeGroup, durationLabel } from "@trackmark/core";
import { RankTable, rankTableStyles as table } from "../design/RankTable.js";
import { RatingStrip } from "../design/RatingStrip.js";
import styles from "./Playlist.module.css";

/**
 * One saved list, run now.
 *
 * A dynamic list is a QUERY. Its contents are computed on every read, so a song rated this morning
 * is in the list this afternoon — nothing is rebuilt and nothing goes stale. That is the whole
 * difference between it and a playlist, and it is why this screen has no Regenerate: there is
 * nothing to regenerate.
 *
 * The builder's preview shows the first twelve. This is what "412 songs match" was promising.
 *
 * It borrows the playlist detail's stylesheet rather than growing a second one. The two screens
 * are the same object — a name, a recipe, a table — and the difference between them is what the
 * words say, not how they are set.
 */

export interface ListSong {
  id: number;
  title: string;
  artist: string;
  artistId: number;
  album: string;
  albumId: number;
  year: number | null;
  durationMs: number | null;
  score: number | null;
  art: string | null;
}

export interface ListDetail {
  id: number;
  name: string;
  rules: RuleGroup;
  createdAt: string;
  itemCount: number;
  songs: ListSong[];
}

export interface ListProps {
  list: ListDetail;
  onEdit: () => void;
  onDelete: () => void;
}

const COLUMNS = "40px 24px 1fr 200px 120px 42px 56px";

const trackLength = (ms: number | null) =>
  ms === null ? "—" : `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`;

export function List({ list, onEdit, onDelete }: ListProps) {
  const durationMs = list.songs.reduce((total, s) => total + (s.durationMs ?? 0), 0);

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div className={styles.title} data-testid="list-title">
          {list.name}
        </div>
        <Link to="/lists" className={styles.back} data-testid="all-lists">
          ← All lists
        </Link>
      </div>

      <div className={styles.origin} data-testid="list-origin">
        <span data-testid="list-count">{list.itemCount.toLocaleString()} SONGS</span>
        <span className={styles.dot}>·</span>
        {durationLabel(durationMs).toUpperCase()}
        <span className={styles.dot}>·</span>
        {/* Said out loud, because a list that looks like a playlist invites the assumption that
            it was fixed at some point. It was not. */}
        RUN JUST NOW · THIS LIST IS A QUERY, NOT A SNAPSHOT
      </div>

      <div className={styles.recipe} data-testid="list-recipe">
        <div className={styles.recipeLabel}>THE RULES</div>
        <div className={styles.recipeRules} data-testid="list-rules">
          {describeGroup(list.rules)}
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.primary} data-testid="edit-list" onClick={onEdit}>
            Edit rules →
          </button>
          <span className={styles.actionNote}>CHANGING THE RULES CHANGES WHAT THIS LIST IS, EVERYWHERE</span>
          <button type="button" className={styles.remove} data-testid="delete-list" onClick={onDelete}>
            Delete
          </button>
        </div>
      </div>

      {list.songs.length === 0 ? (
        <div className={styles.empty} data-testid="list-empty">
          <div className={styles.emptyTitle}>Nothing matches yet</div>
          <div className={styles.emptyHint}>
            THE RULES ARE FINE — NOTHING IN THE LIBRARY ANSWERS THEM TODAY. RATE MORE, OR LOOSEN THEM.
          </div>
        </div>
      ) : (
        <RankTable
          rows={list.songs}
          columns={COLUMNS}
          rowKey={(s) => s.id}
          rowProps={(s) => ({ "data-testid": "list-song", "data-state": s.score === null ? "unrated" : undefined })}
          header={
            <>
              <div>#</div>
              <div />
              <div>TITLE</div>
              <div>ARTIST</div>
              <div>RATING</div>
              <div style={{ textAlign: "right" }}>SCR</div>
              <div style={{ textAlign: "right" }}>DUR</div>
            </>
          }
          renderRow={(s, index) => (
            <>
              {/* A position in today's ordering, not a rank — the list is sorted by score and
                  says so, and calling this RANK would claim a place in the library. */}
              <div className={styles.position}>{index + 1}</div>
              <Link
                to="/album/$albumId"
                params={{ albumId: String(s.albumId) }}
                aria-label={`Go to ${s.album}`}
                data-testid="song-album-link"
              >
                {s.art ? (
                  <img className={table.art} src={s.art} alt="" />
                ) : (
                  <div className={table.artFallback}>{s.album.slice(0, 8)}</div>
                )}
              </Link>
              <div className={table.title}>{s.title}</div>
              <Link to="/artist/$artistId" params={{ artistId: String(s.artistId) }} className={table.secondaryLink}>
                {s.artist}
              </Link>
              <RatingStrip value={s.score} />
              <div className={table.score}>{s.score ?? "—"}</div>
              <div className={table.meta}>{trackLength(s.durationMs)}</div>
            </>
          )}
        />
      )}
    </div>
  );
}
